import { useMemo, useState } from 'react';
import includes from 'lodash/includes';
import { Box, Divider, IconButton, ZillowLogo } from '@zillow/constellation-10';
import {
  IconArchiveFilled,
  IconArchiveOutline,
  IconBankOutline,
  IconCloseOutline,
  IconDollarSignCircleOutline,
  IconEditFormFilled,
  IconEditFormOutline,
  IconExternalOutline,
  IconHammerFilled,
  IconHammerOutline,
  IconHomeTypeOutline,
  IconMenuOutline,
  IconMessageOutline,
  IconTrendingOutline,
  IconUserGroupFilled,
  IconUserGroupOutline,
} from '@zillow/constellation-icons';

import { PathsManifest } from '@zg-rentals/paths-manifest';
import { useRentalManagerUserTrackingWebApiHandler_event } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { RentalManagerUserTrackingEvent } from '@zg-rentals/ts-rental-manager-api-beta-client';

import HeaderNavigationClickstreamEvents from '../../utils/clickstream';
import { getNavDisplays, getPaymentsPath } from '../../utils/displays';
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

import AdaptiveAnchor from '../AdaptiveAnchor';
import LogoZRMStacked from '../Logo/LogoZRMStacked';
import NavigationItem from '../NavigationItem';
import NotificationCenter from '../NotificationCenter';
import PlusIconPropertyMenu from '../PlusIconPropertyMenu';

import type { Props as HeaderNavigationProps } from '../HeaderNavigation';
import type { User } from '../../types';

type Props = Partial<HeaderNavigationProps> & {
  altLogo?: boolean;

  userModel: User;
};

const MobileHeaderNav = ({
  altLogo = true,
  brand = 'zillow',
  pathname = '',
  useRelativePaths,
  reactRouterLink,
  host,
  userModel = {} as User,
  ...rest
}: Props) => {
  const [isMobileMenuExpanded, setIsMobileMenuExpanded] = useState(false);

  const { displays } = getNavDisplays({ ...rest, userModel });
  const {
    displayAnalytics,
    displayBilling,
    displayMFHResources,
    displayMainAccountSettings,
    displayMainBilling,
    displayMainHelp,
    displayMainLeadManagement,
    displayMainLeads,
    displayMainMaintenanceTracker,
    displayMainMessages,
    displayMainMyProperties,
    displayMainNotificationCenter,
    displayMainPayments,
    displayMainResources,
    displayOnboardingTasks,
    displayUserManagement,
  } = displays;
  const { features = {}, loggedIn, paymentsAcceptedCount, hasSeenNewPaymentsPage } = userModel;
  const { canViewClaimFeedListing: canAccessAddFeedListingsModal = false, canViewOnboardingFlow = false } =
    features as User['features'];

  const { escapeAllLinks, webSocketInstance } = rest;

  // Pass link component from either react-router or next-js
  const Link = reactRouterLink;

  const useAbsolutePaths = !useRelativePaths || !reactRouterLink;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, host).getAllPaths();

  const { mutate: trackEvent } = useRentalManagerUserTrackingWebApiHandler_event();

  const {
    adManagementPage,
    analyticsOverview,
    analyticsPage,
    billingDue,
    billingPage,
    helpPage = '',
    homePage,
    inboxPage,
    leadManagementPage,
    leadsPage,
    maintenanceTrackerPage,
    mfhResourcesPage,
    paymentsAggregatePage,
    paymentsGetStartedPage,
    rentPaymentPage,
    payments,
    rentPayment,
    postLogout = '',
    propertiesPage,
    resourcesPage,
    setupTasks,
    userManagementPage,
    userSettingsPage = '',
    adManagementTabsPage,
  } = paths;

  const isPageActive = useMemo(() => {
    return ({ pageUrl }: { pageUrl: string }) => {
      return includes(pathname, pageUrl);
    };
  }, [pathname]);

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];
      if (pageClickingTo === HeaderTitles.payments && hasSeenNewPaymentsPage === false) {
        trackEvent({ getParams: { trackingEvent: RentalManagerUserTrackingEvent.OPEN_NEW_PAYMENTS_PAGE } });
      }

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      }
    }

    setIsMobileMenuExpanded(false);
  };

  const handleClickPropertyMenuItem = (event: React.SyntheticEvent) => {
    // prevent parent navigation
    event.stopPropagation();

    handleLinkClick(HeaderTitles.myProperties);
  };

  const isPropertiesPageActive = includes(pathname, propertiesPage);
  const isPaymentPageActive = includes(pathname, payments) || includes(pathname, rentPayment);

  return (
    <Box
      data-testid="MobileHeaderNavContainer"
      css={{
        position: isMobileMenuExpanded ? 'fixed' : 'relative',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'bg.neutral',
        width: '100%',
        height: '100%',
        zIndex: 1000,
      }}
    >
      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '55px',
          padding: '400',
        }}
      >
        <IconButton
          emphasis="bare"
          shape="square"
          size="md"
          title={`${isMobileMenuExpanded ? 'Close' : 'Open'} menu`}
          tone="brand"
          icon={isMobileMenuExpanded ? <IconCloseOutline /> : <IconMenuOutline />}
          onClick={() => setIsMobileMenuExpanded(!isMobileMenuExpanded)}
        />

        <Box css={{ display: 'flex', alignItems: 'center', width: altLogo ? '136px' : '120px' }}>
          <AdaptiveAnchor
            escape={escapeAllLinks}
            link={Link}
            onClick={() => setIsMobileMenuExpanded(false)}
            path={propertiesPage}
            useAbsolutePaths={useAbsolutePaths}
            title="Return to properties page"
          >
            {altLogo ? <LogoZRMStacked /> : <ZillowLogo showLegacyLogo={true} />}
          </AdaptiveAnchor>
        </Box>

        <>
          {loggedIn && displayMainNotificationCenter ? (
            <NotificationCenter brand={brand} webSocketInstance={webSocketInstance} />
          ) : (
            // need an element here to layout the other elements via flex correctly
            <div />
          )}
        </>
      </Box>

      {isMobileMenuExpanded && (
        <Box
          css={{
            paddingTop: '500',
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          {/* properties */}
          {loggedIn && displayMainMyProperties && (
            <NavigationItem
              isActive={isPropertiesPageActive}
              isMobileWeb={true}
              title={HeaderTitles.myProperties}
              escape={escapeAllLinks}
              path={propertiesPage}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconHomeTypeOutline />}
              InactiveIcon={<IconHomeTypeOutline />}
              TrailingIcon={
                <PlusIconPropertyMenu
                  Link={Link}
                  canAccessAddFeedListingsModal={canAccessAddFeedListingsModal}
                  escapeAllLinks={escapeAllLinks}
                  handleNoFeedIconPlusClick={handleLinkClick}
                  onClickPropertyMenuItem={handleClickPropertyMenuItem}
                  propertiesPage={propertiesPage}
                  useAbsolutePaths={useAbsolutePaths}
                  userModel={userModel}
                />
              }
              onClick={() => {
                handleLinkClick(HeaderTitles.myProperties);
              }}
            />
          )}

          {/* leads */}
          {displayMainLeadManagement && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: leadManagementPage })}
              isMobileWeb={true}
              title={HeaderTitles.leadManagement}
              escape={true}
              path={leadManagementPage}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconUserGroupFilled />}
              InactiveIcon={<IconUserGroupOutline />}
              TrailingIcon={null}
              newTagCookieName="hasSeenLeadManagement"
              showNewTag={!canViewOnboardingFlow}
              onClick={() => {
                handleLinkClick(HeaderTitles.leadManagement);
              }}
            />
          )}

          {/* messages */}
          {displayMainMessages && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: inboxPage })}
              isMobileWeb={true}
              title={HeaderTitles.messages}
              escape={true}
              path={inboxPage}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconMessageOutline />}
              InactiveIcon={<IconMessageOutline />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.messages);
              }}
            />
          )}

          {/* maintenance */}
          {displayMainMaintenanceTracker && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: maintenanceTrackerPage })}
              isMobileWeb={true}
              title={HeaderTitles.maintenanceTracker}
              escape={true}
              path={maintenanceTrackerPage}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconHammerFilled />}
              InactiveIcon={<IconHammerOutline />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.maintenanceTracker);
              }}
            />
          )}

          {/* contacts */}
          {displayMainLeads && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: leadsPage })}
              title={HeaderTitles.leads}
              escape={true}
              path={leadsPage}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconMessageOutline />}
              InactiveIcon={<IconMessageOutline />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.leads);
              }}
            />
          )}

          {/* payments */}
          {loggedIn && displayMainPayments && (
            <NavigationItem
              isActive={isPropertiesPageActive ? false : isPaymentPageActive}
              title={HeaderTitles.payments}
              escape={true}
              path={getPaymentsPath({
                paymentsAcceptedCount,
                paymentsAggregatePage,
                paymentsGetStartedPage,
                rentPaymentPage,
              })}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconDollarSignCircleOutline />}
              InactiveIcon={<IconDollarSignCircleOutline />}
              onClick={() => {
                handleLinkClick(HeaderTitles.payments);
              }}
            />
          )}

          {/* analytics */}
          {displayAnalytics && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: analyticsPage })}
              title={HeaderTitles.analytics}
              escape={true}
              path={`${analyticsPage}/${analyticsOverview}`}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconTrendingOutline />}
              InactiveIcon={<IconTrendingOutline />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.analytics);
              }}
            />
          )}

          {/* billing */}
          {displayMainBilling && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: billingPage })}
              title={HeaderTitles.billing}
              escape={true}
              path={`${billingPage}/${billingDue}`}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconBankOutline />}
              InactiveIcon={<IconBankOutline />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.billing);
              }}
            />
          )}

          {/* ad management */}
          {loggedIn && userModel.isEnterpriseCustomer && (
            <NavigationItem
              isActive={includes(pathname, adManagementTabsPage)}
              title={HeaderTitles.adManagement}
              escape={true}
              path={`${adManagementPage}/${adManagementTabsPage}`}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              InactiveIcon={<IconArchiveOutline />}
              ActiveIcon={<IconArchiveFilled />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.adManagement);
              }}
            />
          )}

          {/* onboarding tasks */}
          {displayOnboardingTasks && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: setupTasks })}
              title={HeaderTitles.onboardingTasks}
              escape={true}
              path={`${adManagementPage}/${setupTasks}`}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconEditFormFilled />}
              InactiveIcon={<IconEditFormOutline />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.onboardingTasks);
              }}
            />
          )}

          <Divider css={{ marginBlock: '400', flexShrink: 0 }} />

          {/* account settings */}
          {displayMainAccountSettings && (
            <NavigationItem
              title={HeaderTitles.accountSettings}
              path={userSettingsPage}
              target="_blank"
              TrailingIcon={<IconExternalOutline />}
              onClick={() => handleLinkClick(HeaderTitles.accountSettings)}
            />
          )}

          {/* billing - premium listing */}
          {displayBilling && !displayMainBilling && (
            <NavigationItem
              title={HeaderTitles.billing}
              path={`${billingPage}/${billingDue}`}
              noMarginBottom={true}
              onClick={() => handleLinkClick(HeaderTitles.billing)}
            />
          )}

          {/* user management  */}
          {displayUserManagement && (
            <NavigationItem
              title={HeaderTitles.userManagement}
              path={userManagementPage}
              onClick={() => handleLinkClick(HeaderTitles.userManagement)}
            />
          )}

          <Divider css={{ marginBlock: '400', flexShrink: 0 }} />

          {/* resources */}
          {Boolean(displayMFHResources || displayMainResources) && (
            <>
              {displayMFHResources ? (
                <NavigationItem
                  title={HeaderTitles.mfhResources}
                  path={mfhResourcesPage}
                  target="_blank"
                  TrailingIcon={<IconExternalOutline />}
                  onClick={() => handleLinkClick(HeaderTitles.mfhResources)}
                />
              ) : (
                <NavigationItem
                  title={HeaderTitles.resources}
                  path={resourcesPage}
                  target="_blank"
                  TrailingIcon={<IconExternalOutline />}
                  onClick={() => handleLinkClick(HeaderTitles.resources)}
                />
              )}
            </>
          )}

          {/* help */}
          {displayMainHelp && (
            <NavigationItem
              title={HeaderTitles.help}
              path={helpPage}
              target="_blank"
              TrailingIcon={<IconExternalOutline />}
              onClick={() => handleLinkClick(HeaderTitles.help)}
            />
          )}

          <Divider css={{ marginBlock: '400', flexShrink: 0 }} />

          {/* go to Zillow  */}
          <NavigationItem
            title={HeaderTitles.backToBrand}
            path={homePage}
            target="_blank"
            TrailingIcon={<IconExternalOutline />}
            onClick={() => handleLinkClick(HeaderTitles.backToBrand)}
          />

          {/* sign out  */}
          <NavigationItem
            title={HeaderTitles.signOut}
            path={postLogout}
            escape={true}
            onClick={() => handleLinkClick(HeaderTitles.signOut)}
          />
        </Box>
      )}
    </Box>
  );
};

export default MobileHeaderNav;
